import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ClearIcon from "@mui/icons-material/Clear";
import PublishIcon from "@mui/icons-material/Publish";
import debounce from "lodash/debounce";
import ColorService from "../../../services/ColorService";
import StatsCrossService from "../../../services/StatsCrossService";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import PuzzleCard from "../../pzl_common/board/PuzzleCard";
import {
    adminTagFilterAutocompleteSlotProps,
    adminTagFilterChipSx,
} from "../../pzl_common/board/adminTagFilterChipSx";
import AdminWordWheelBoardService from "./AdminWordWheelBoardService";
import Logger from "../../../services/Logger";

const stepInfo = {
    0: {
        title: "Draft Stage",
        description: "This is where puzzle content is initially created and worked on. Items in this stage are not yet ready for review."
    },
    1: {
        title: "Ready to Review",
        description: "Puzzle content has been completed and is ready for review by editors. All necessary components should be in place."
    },
    2: {
        title: "Reviewed",
        description: "Content has been reviewed and approved by editors. Any necessary changes have been made and verified."
    },
    3: {
        title: "Published",
        description: "Puzzle content is now live and available to users. Content is in its final published state."
    },
    4: {

    }
};

// Map step number to state string
const getStateFromStep = (step) => {
    const stepToStateMap = {
        0: 'DRAFT',
        1: 'READY_TO_REVIEW',
        2: 'REVIEWED',
        3: 'PUBLISHED'
    };
    return stepToStateMap[step] || null;
};

export default function Step0SelectDraft({
    step,
    stepState,
    selectedGenreId = "all",
    selectedTags = [],
    availableTags = [],
    tagsLoading = false,
    selectedFree = null,
    selectedLevel = "all",
    onTagsChange,
    onFreeChange,
    onLevelChange,
    initialKeyword,
    onKeywordChange,
    handlePuzzleClick
}) {
    const info = stepInfo[step] || { title: "Unknown step", description: "" };
    const { language } = useAdminLanguage();
    const navigate = useNavigate();

    const [overviewStats, setOverviewStats] = useState(null);
    const [puzzleData, setPuzzleData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15
    });
    const [totalElements, setTotalElements] = useState(0);
    const sort = useMemo(() => {
        if (step === 3 || stepState === "PUBLISHED") {
            return [
                { field: "publishedAt", sort: "desc" },
                { field: "updatedAt", sort: "desc" },
            ];
        }
        return [
            { field: "updatedAt", sort: "desc" },
            { field: "createdAt", sort: "desc" },
        ];
    }, [step, stepState]);
    const [keywordInput, setKeywordInput] = useState(() => (initialKeyword != null && initialKeyword !== "" ? String(initialKeyword) : ""));
    const [keywordDebounced, setKeywordDebounced] = useState(() => (initialKeyword != null && initialKeyword !== "" ? String(initialKeyword) : ""));

    const debouncedSetKeyword = useMemo(
        () =>
            debounce((value) => {
                setKeywordDebounced(value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }, 300),
        []
    );

    useEffect(() => () => debouncedSetKeyword.cancel(), [debouncedSetKeyword]);

    const clearKeyword = useCallback(() => {
        debouncedSetKeyword.cancel();
        setKeywordInput("");
        setKeywordDebounced("");
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, [debouncedSetKeyword]);

    // Sync persisted keyword from parent when it changes (e.g. from storage); only update if different to avoid triggering fetch/reset again
    const initialKeywordStr = initialKeyword != null ? String(initialKeyword) : "";
    useEffect(() => {
        setKeywordInput((prev) => (prev !== initialKeywordStr ? initialKeywordStr : prev));
        setKeywordDebounced((prev) => (prev !== initialKeywordStr ? initialKeywordStr : prev));
    }, [initialKeywordStr]);

    // Persist debounced keyword to parent (saves to localStorage); skip first run to avoid clearing saved value on mount.
    // Use ref for callback so this effect only runs when keywordDebounced changes, not when parent re-renders and passes a new function.
    const onKeywordChangeRef = useRef(onKeywordChange);
    onKeywordChangeRef.current = onKeywordChange;
    const persistKeywordRanRef = useRef(false);
    useEffect(() => {
        if (!persistKeywordRanRef.current) {
            persistKeywordRanRef.current = true;
            return;
        }
        if (onKeywordChangeRef.current) onKeywordChangeRef.current(keywordDebounced);
    }, [keywordDebounced]);

    // Fetch puzzle data
    useEffect(() => {
        // Skip if stepState is invalid
        if (!stepState) {
            return;
        }

        const fetchPuzzleData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                Logger.log("Fetching puzzles for state:", stepState, "step:", step);
                
                await AdminWordWheelBoardService.requestList(
                    paginationModel,
                    sort,
                    setPuzzleData,
                    setTotalElements,
                    keywordDebounced,
                    stepState,
                    selectedGenreId,
                    language,
                    selectedTags,
                    selectedFree,
                    selectedLevel
                );
            } catch (err) {
                Logger.error("Error fetching puzzle data:", err);
                setError("Failed to load puzzle data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchPuzzleData();
    }, [step, stepState, paginationModel, sort, keywordDebounced, selectedGenreId, language, selectedTags, selectedFree, selectedLevel]);

    // Reset pagination when step, genre, tags, free, or language change
    useEffect(() => {
        setPaginationModel({
            page: 0,
            pageSize: 15
        });
        setPuzzleData([]);
        setTotalElements(0);
    }, [step, selectedGenreId, selectedTags, selectedFree, selectedLevel, language, keywordDebounced]);

    // Fetch state count stats (total and crossword count)
    useEffect(() => {
        const state = getStateFromStep(step);
        if (!state) return;
        const fetchStateCountStats = async () => {
            try {
                const stats = await StatsCrossService.getStateCountStats(state, "WORD_WHEEL");
                setOverviewStats(stats);
            } catch (err) {
                Logger.error("Error fetching state count stats:", err);
            }
        };
        fetchStateCountStats();
    }, [step]);

    const handlePageChange = (_event, newPage) => {
        setPaginationModel(prev => ({
            ...prev,
            page: newPage - 1 // MUI Pagination uses 1-based indexing
        }));
    };

    const totalElementsCount = totalElements ?? 0;
    const totalPages = totalElementsCount > 0 ? Math.ceil(totalElementsCount / paginationModel.pageSize) : 0;

    return (
        <Box sx={{ p: 0 }}>
            <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
                {/* First Column: Title and Description */}
                <Box sx={{ flex: '1 1 50%' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {info.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {info.description}
                    </Typography>
                </Box>

                {/* Reviewed: Quick Publisher sits in front of the Total box */}
                {(step === 2 || (overviewStats && (overviewStats.countByDifficulty || overviewStats.countByType))) && (
                    <Box sx={{ flex: '1 1 50%', display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {step === 2 && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PublishIcon />}
                                    onClick={() => navigate("/admin/puzzle/word_wheel_board/quick-publish")}
                                    sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
                                >
                                    Quick Publisher
                                </Button>
                            )}
                            {overviewStats?.countByDifficulty && (
                                <>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minWidth: 60, textAlign: 'center', backgroundColor: ColorService.getStateColor('published') }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>Total</Typography>
                                        <Typography variant="h6" sx={{ color: 'white' }}>
                                            {overviewStats.countByDifficulty.total}
                                        </Typography>
                                    </Box>
                                    {overviewStats.countByDifficulty.EASY > 0 && (
                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minWidth: 60, textAlign: 'center', backgroundColor: ColorService.getDifficultyColor('easy') }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>Easy</Typography>
                                            <Typography variant="h6" sx={{ color: 'white' }}>
                                                {overviewStats.countByDifficulty.EASY}
                                            </Typography>
                                        </Box>
                                    )}
                                    {overviewStats.countByDifficulty.MEDIUM > 0 && (
                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minWidth: 60, textAlign: 'center', backgroundColor: ColorService.getDifficultyColor('medium') }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>Medium</Typography>
                                            <Typography variant="h6" sx={{ color: 'white' }}>
                                                {overviewStats.countByDifficulty.MEDIUM}
                                            </Typography>
                                        </Box>
                                    )}
                                    {overviewStats.countByDifficulty.HARD > 0 && (
                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minWidth: 60, textAlign: 'center', backgroundColor: ColorService.getDifficultyColor('hard') }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'white' }}>Hard</Typography>
                                            <Typography variant="h6" sx={{ color: 'white' }}>
                                                {overviewStats.countByDifficulty.HARD}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Level, Free/Paid, and Tags Filters - Always visible */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Level</InputLabel>
                    <Select
                        value={selectedLevel || "all"}
                        label="Level"
                        onChange={(e) => onLevelChange && onLevelChange(e.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="EASY">Easy</MenuItem>
                        <MenuItem value="MEDIUM">Medium</MenuItem>
                        <MenuItem value="HARD">Hard</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Free/Paid</InputLabel>
                    <Select
                        value={selectedFree === null ? "all" : selectedFree === true ? "free" : "paid"}
                        label="Free/Paid"
                        onChange={(e) => {
                            const value = e.target.value;
                            if (onFreeChange) {
                                if (value === "all") {
                                    onFreeChange(null);
                                } else if (value === "free") {
                                    onFreeChange(true);
                                } else {
                                    onFreeChange(false);
                                }
                            }
                        }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="free">Free</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                    </Select>
                </FormControl>
                <Autocomplete
                    multiple
                    size="small"
                    options={availableTags}
                    value={selectedTags.map((s) => availableTags.find((o) => (typeof o === "string" ? o : (o.tag ?? o.name ?? String(o))) === s) ?? s)}
                    isOptionEqualToValue={(opt, val) => (typeof opt === "string" ? opt : (opt.tag ?? opt.name ?? String(opt))) === (typeof val === "string" ? val : (val.tag ?? val.name ?? String(val)))}
                    onChange={(event, newValue) => {
                        const normalizedTags = newValue.map((tag) => (typeof tag === "string" ? tag : (tag.tag ?? tag.name ?? String(tag))));
                        onTagsChange && onTagsChange(normalizedTags);
                    }}
                    loading={tagsLoading}
                    slotProps={adminTagFilterAutocompleteSlotProps}
                    sx={{
                        minWidth: 200,
                        maxWidth: 260,
                        width: { xs: "100%", sm: 220 },
                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const tagLabel = typeof option === "string" ? option : (option.tag ?? option.name ?? String(option));
                            return (
                                <Chip
                                    variant="outlined"
                                    label={tagLabel}
                                    {...getTagProps({ index })}
                                    sx={adminTagFilterChipSx}
                                />
                            );
                        })
                    }
                    getOptionLabel={(option) => {
                        const name = typeof option === "string" ? option : (option.tag ?? option.name ?? String(option));
                        const count = typeof option === "object" && option != null ? option.count : undefined;
                        if (count != null && count !== "") return `${name} (${count})`;
                        return name;
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Filter by Tags"
                            placeholder="Select tags"
                        />
                    )}
                />
                <TextField
                    size="small"
                    label="Keyword"
                    placeholder="Search by keyword"
                    value={keywordInput}
                    onChange={(e) => {
                        setKeywordInput(e.target.value);
                        debouncedSetKeyword(e.target.value);
                    }}
                    inputProps={{ maxLength: 128 }}
                    slotProps={{
                        input: {
                            endAdornment: keywordInput ? (
                                <InputAdornment position="end" sx={{ mr: -0.5 }}>
                                    <IconButton
                                        type="button"
                                        size="small"
                                        edge="end"
                                        aria-label="Clear keyword"
                                        onClick={clearKeyword}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    sx={{ flex: 1, minWidth: { xs: "100%", sm: 280 } }}
                />
            </Box>

            {loading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 200
                    }}
                >
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading puzzles...
                    </Typography>
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : puzzleData.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 200
                    }}
                >
                    <Typography variant="body1">
                        No puzzles in this stage
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {puzzleData.length} {totalElementsCount > 0 ? `of ${totalElementsCount}` : ''} puzzles
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2
                    }}>
                        {puzzleData.map((puzzle) => (
                            <Box 
                                key={puzzle.id} 
                                sx={{ 
                                    minWidth: '500px',
                                    maxWidth: '500px',
                                    flex: '0 0 auto'
                                }}
                            >
                                <PuzzleCard
                                    puzzle={puzzle}
                                    onClick={handlePuzzleClick}
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={paginationModel.page + 1}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

        </Box>
    );
}

